import { usePageRefresh } from '../../../context/PageRefreshContext';
import { useState, useMemo, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getTasksList, deleteTask, getProjectsList } from '../../../redux/actions/SocietyPortal/TaskAction/TaskAction';
import Pagination from '../../../components/common/Pagination';
import SearchableDropdown from '../../../components/SearchableDropdown';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import TaskTabs from './TaskTabs';
import {
    TASK_STATUSES, TASK_PRIORITIES,
    statusBadgeClass, priorityBadgeClass, humanize,
} from '../../../consts/taskConstants';

const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isOverdue = (task) =>
    task.due_date && !['COMPLETED', 'CLOSED', 'CANCELLED'].includes(task.status) &&
    new Date(task.due_date).getTime() < Date.now();

const assigneeName = (u) => (u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '');
const assigneeInitials = (u) => {
    if (!u) return '?';
    const s = `${(u.first_name || '').charAt(0)}${(u.last_name || '').charAt(0)}`.toUpperCase();
    return s || (u.email || 'U').charAt(0).toUpperCase();
};

const Task = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const {
        tasksListData, tasksListLoading, tasksPagination,
        deleteTaskLoading, projectsListData, projectsListLoading,
    } = useSelector((state) => state.TaskReducer);

    // Dashboard tiles drill down into this list via the query string, so the
    // filters seed from the URL on first render.
    const [searchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
    const [filterProject, setFilterProject] = useState(searchParams.get('project_id') || '');
    // A drill-down can pass several statuses at once (the API takes them comma
    // separated, e.g. Completed = COMPLETED,CLOSED). That has no single-select
    // equivalent, so it lives beside the dropdown as a chip.
    const urlStatus = searchParams.get('status') || '';
    const [filterStatus, setFilterStatus] = useState(urlStatus.includes(',') ? '' : urlStatus);
    const [statusGroup, setStatusGroup] = useState(urlStatus.includes(',') ? urlStatus : '');
    const [filterPriority, setFilterPriority] = useState(searchParams.get('priority') || '');
    // These three have no dropdown of their own — they arrive from a drill-down
    // and show as removable chips so the narrowing is never invisible.
    const [filterDue, setFilterDue] = useState(searchParams.get('due') || '');
    const [filterDepartment, setFilterDepartment] = useState(searchParams.get('department') || '');
    const [filterAssignee, setFilterAssignee] = useState(searchParams.get('assigned_to') || '');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [taskToDelete, setTaskToDelete] = useState(null);

    // Load projects once to populate the project filter dropdown.
    useEffect(() => {
        dispatch(getProjectsList({}));
    }, [dispatch]);

    const fetchTasks = useMemo(() => () => {
        dispatch(getTasksList({
            page: currentPage,
            limit: itemsPerPage,
            search: searchQuery || undefined,
            project_id: filterProject || undefined,
            status: statusGroup || filterStatus || undefined,
            priority: filterPriority || undefined,
            due: filterDue || undefined,
            department: filterDepartment || undefined,
            assigned_to: filterAssignee || undefined,
            sort_by: 'created_at',
            sort_dir: 'desc',
        }));
    }, [dispatch, currentPage, itemsPerPage, searchQuery, filterProject, filterStatus, statusGroup,
        filterPriority, filterDue, filterDepartment, filterAssignee]);
    usePageRefresh(fetchTasks);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const tasks = Array.isArray(tasksListData) ? tasksListData : [];

    const projectOptions = useMemo(
        () => (Array.isArray(projectsListData) ? projectsListData : []).map((p) => ({ value: String(p.project_id), label: p.name })),
        [projectsListData],
    );
    const statusOptions = useMemo(() => TASK_STATUSES.map((s) => ({ value: s, label: humanize(s) })), []);
    const priorityOptions = useMemo(() => TASK_PRIORITIES.map((p) => ({ value: p, label: humanize(p) })), []);

    const handleFilterChange = (setter, value) => {
        setter(value);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setSearchQuery('');
        setFilterProject('');
        setFilterStatus('');
        setStatusGroup('');
        setFilterPriority('');
        setFilterDue('');
        setFilterDepartment('');
        setFilterAssignee('');
        setCurrentPage(1);
    };

    const hasActiveFilters = searchQuery || filterProject || filterStatus || statusGroup || filterPriority
        || filterDue || filterDepartment || filterAssignee;

    // Chips for the filters that have no control in the bar above.
    const extraChips = [
        statusGroup && {
            key: 'statusGroup',
            label: statusGroup.split(',').map((s) => humanize(s.trim())).join(' / '),
            clear: () => setStatusGroup(''),
        },
        filterDue && { key: 'due', label: filterDue === 'overdue' ? 'Overdue' : 'Due today', clear: () => setFilterDue('') },
        filterDepartment && { key: 'department', label: humanize(filterDepartment), clear: () => setFilterDepartment('') },
        filterAssignee && { key: 'assignee', label: `Assignee #${filterAssignee}`, clear: () => setFilterAssignee('') },
    ].filter(Boolean);

    const handleDeleteConfirm = () => {
        if (!taskToDelete) return;
        dispatch(deleteTask({
            taskId: taskToDelete.task_id,
            onSuccess: () => {
                setDeleteModalOpen(false);
                setTaskToDelete(null);
                fetchTasks();
            },
        }));
    };

    const totalPages = tasksPagination?.totalPages || 1;

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Task</h1>
                    <p className="tw-text-xs tw-text-gray-500">All tasks across projects</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {can(PERMISSIONS.TASK_CREATE) && (
                        <button
                            onClick={() => navigate('/society-portal/tasks/add')}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-bg-blue-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700"
                        >
                            <Icon icon="mdi:plus" /> New Task
                        </button>
                    )}
                </div>
            </div>

            <TaskTabs active="tasks" />

            {/* Filter Bar */}
            <div className="tw-bg-white tw-rounded-lg tw-py-2.5 tw-px-3 tw-mb-4 tw-shadow-sm tw-border tw-border-gray-100 tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                <div className="tw-flex-1 tw-min-w-[200px] tw-relative">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        placeholder="Search by title, number or description..."
                        value={searchQuery}
                        onChange={(e) => handleFilterChange(setSearchQuery, e.target.value)}
                        className="tw-w-full tw-pl-8 tw-pr-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500"
                    />
                </div>
                <div className="tw-w-[180px]">
                    <SearchableDropdown value={filterProject} onChange={(v) => handleFilterChange(setFilterProject, v === 'all' ? '' : v)}
                        options={projectOptions} loading={projectsListLoading} placeholder="All Projects" small />
                </div>
                <div className="tw-w-[150px]">
                    <SearchableDropdown value={filterStatus}
                        onChange={(v) => { setStatusGroup(''); handleFilterChange(setFilterStatus, v === 'all' ? '' : v); }}
                        options={statusOptions} placeholder="All Status" small />
                </div>
                <div className="tw-w-[140px]">
                    <SearchableDropdown value={filterPriority} onChange={(v) => handleFilterChange(setFilterPriority, v === 'all' ? '' : v)}
                        options={priorityOptions} placeholder="All Priority" small />
                </div>
                {extraChips.map((chip) => (
                    <span key={chip.key} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-md tw-bg-blue-50 tw-border tw-border-blue-100 tw-text-blue-700 tw-text-[11px] tw-font-medium">
                        {chip.label}
                        <button onClick={() => { chip.clear(); setCurrentPage(1); }} title="Remove filter" className="hover:tw-text-blue-900">
                            <Icon icon="mdi:close" className="tw-text-xs" />
                        </button>
                    </span>
                ))}
                {hasActiveFilters && (
                    <button onClick={clearFilters} className="tw-flex tw-items-center tw-gap-1 tw-text-red-500 tw-text-xs tw-font-medium hover:tw-text-red-600">
                        <Icon icon="mdi:close" className="tw-text-sm" /> Clear
                    </button>
                )}
            </div>

            {/* Loading */}
            {tasksListLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-text-center tw-py-12">
                    <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                    <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading tasks...</p>
                </div>
            )}

            {/* Table */}
            {!tasksListLoading && (
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-sm tw-border-collapse">
                        <thead>
                            <tr className="tw-bg-gray-50/80 tw-border-b tw-border-gray-200">
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Task</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Project</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Priority</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Assignee</th>
                                <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Due</th>
                                <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-gray-100">
                            {tasks.map((task) => {
                                const name = assigneeName(task.assignee);
                                return (
                                <tr key={task.task_id} className="hover:tw-bg-blue-50/40 tw-transition-colors">
                                    <td className="tw-px-3 tw-py-1 tw-max-w-[320px]">
                                        <button onClick={() => navigate(`/society-portal/tasks/view/${task.task_id}`)} className="tw-text-left tw-flex tw-items-baseline tw-gap-1.5 tw-w-full tw-group tw-truncate">
                                            <span className="tw-font-medium tw-text-gray-800 tw-truncate group-hover:tw-text-blue-600">{task.title}</span>
                                            <span className="tw-text-[10px] tw-text-gray-400 tw-font-mono tw-flex-shrink-0">({task.task_number})</span>
                                        </button>
                                    </td>
                                    <td className="tw-px-3 tw-py-1">
                                        {task.project?.name
                                            ? <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-indigo-50 tw-text-indigo-700 tw-max-w-[160px] tw-truncate"><Icon icon="hugeicons:folder-01" className="tw-flex-shrink-0" /> <span className="tw-truncate">{task.project.name}</span></span>
                                            : <span className="tw-text-gray-300">—</span>}
                                    </td>
                                    <td className="tw-px-3 tw-py-1">
                                        <span className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-whitespace-nowrap ${statusBadgeClass(task.status)}`}>
                                            {humanize(task.status)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-1">
                                        <span className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-whitespace-nowrap ${priorityBadgeClass(task.priority)}`}>
                                            {humanize(task.priority)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-1">
                                        {name ? (
                                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
                                                <span className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-teal-100 tw-text-teal-700 tw-text-[8px] tw-font-semibold tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">{assigneeInitials(task.assignee)}</span>
                                                <span className="tw-text-gray-600 tw-truncate tw-max-w-[120px]">{name}</span>
                                            </div>
                                        ) : <span className="tw-text-gray-300">—</span>}
                                    </td>
                                    <td className="tw-px-3 tw-py-1 tw-whitespace-nowrap">
                                        <span className={isOverdue(task) ? 'tw-inline-flex tw-items-center tw-gap-1 tw-text-red-600 tw-font-medium' : 'tw-text-gray-600'}>
                                            {isOverdue(task) && <Icon icon="mdi:alert-circle-outline" className="tw-text-xs" />}
                                            {formatDate(task.due_date)}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-1">
                                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-0.5">
                                            <button onClick={() => navigate(`/society-portal/tasks/view/${task.task_id}`)} className="tw-p-1.5 tw-rounded hover:tw-bg-blue-100 tw-text-gray-400 hover:tw-text-blue-600" title="View">
                                                <Icon icon="mdi:eye-outline" />
                                            </button>
                                            {can(PERMISSIONS.TASK_UPDATE) && (
                                                <button onClick={() => navigate(`/society-portal/tasks/edit/${task.task_id}`)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-700" title="Edit">
                                                    <Icon icon="mdi:pencil-outline" />
                                                </button>
                                            )}
                                            {can(PERMISSIONS.TASK_DELETE) && (
                                                <button onClick={() => { setTaskToDelete(task); setDeleteModalOpen(true); }} className="tw-p-1.5 tw-rounded hover:tw-bg-red-100 tw-text-gray-400 hover:tw-text-red-500" title="Delete">
                                                    <Icon icon="mdi:delete-outline" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {tasks.length === 0 && (
                        <div className="tw-text-center tw-py-10">
                            <Icon icon="hugeicons:task-01" className="tw-text-4xl tw-text-gray-300" />
                            <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">No tasks found</p>
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {!tasksListLoading && tasks.length > 0 && (
                <div className="tw-mt-4">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={tasksPagination?.total || tasks.length}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={(value) => { setItemsPerPage(value); setCurrentPage(1); }}
                        itemsPerPageOptions={[10, 20, 50, 100]}
                        itemLabel="tasks"
                    />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:delete-alert" className="tw-text-red-600 tw-text-xl" />
                            </div>
                            <div>
                                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Delete Task</h3>
                                <p className="tw-text-sm tw-text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">
                            Are you sure you want to delete <span className="tw-font-semibold">{taskToDelete?.title}</span>?
                        </p>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button onClick={() => { setDeleteModalOpen(false); setTaskToDelete(null); }} disabled={deleteTaskLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50">
                                Cancel
                            </button>
                            <button onClick={handleDeleteConfirm} disabled={deleteTaskLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50">
                                {deleteTaskLoading
                                    ? <><Icon icon="mdi:loading" className="tw-animate-spin" /> Deleting...</>
                                    : <><Icon icon="mdi:delete" /> Delete</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Task;
